import React from 'react';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const Cancellation: React.FC = () => {
  const cancellationPeriods = [
    {
      period: '60+ days before departure',
      refund: 'Full refund minus processing fees',
      processing: '5-7 business days',
      icon: CheckCircleIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      period: '30-59 days before departure',
      refund: '75% refund',
      processing: '5-7 business days',
      icon: CheckCircleIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      period: '15-29 days before departure',
      refund: '50% refund',
      processing: '5-7 business days',
      icon: ExclamationTriangleIcon,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100'
    },
    {
      period: 'Within 14 days of departure',
      refund: 'No refund',
      processing: 'N/A',
      icon: XCircleIcon,
      color: 'text-red-600',
      bgColor: 'bg-red-100'
    }
  ];

  const specialCircumstances = [
    {
      title: 'Medical Emergencies',
      description: 'Cancellations due to medical emergencies may be eligible for partial refunds or rescheduling.',
      requirements: [
        'Medical certificate from licensed physician',
        'Cancellation within 48 hours of medical emergency',
        'Documentation of inability to travel',
        'May be subject to rescheduling fees'
      ]
    },
    {
      title: 'Force Majeure Events',
      description: 'Natural disasters, political unrest, or other circumstances beyond our control.',
      requirements: [
        'Official government travel advisories',
        'Natural disaster declarations',
        'Pandemic or health emergency restrictions',
        'Full refund or rescheduling options available'
      ]
    },
    {
      title: 'Visa Denials',
      description: 'Cancellations due to visa denials may be eligible for refunds.',
      requirements: [
        'Official visa denial letter from embassy',
        'Cancellation within 7 days of visa denial',
        'Proof of visa application submission',
        'May be subject to processing fees'
      ]
    }
  ];

  const refundProcess = [
    {
      step: 1,
      title: 'Submit Cancellation Request',
      description: 'Contact us via email or phone to initiate cancellation',
      details: [
        'Include booking reference number',
        'Specify reason for cancellation',
        'Provide required documentation if applicable'
      ]
    },
    {
      step: 2,
      title: 'Review and Confirmation',
      description: 'We review your request and confirm cancellation terms',
      details: [
        'Review within 24-48 hours',
        'Confirm refund amount and timeline',
        'Provide cancellation confirmation'
      ]
    },
    {
      step: 3,
      title: 'Refund Processing',
      description: 'Refund is processed according to the timeline',
      details: [
        'Refund issued to original payment method',
        'Processing time: 5-7 business days',
        'Confirmation sent via email'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Hero Section */}
      <div className="bg-primary-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4">Cancellation Policy</h1>
          <p className="text-xl opacity-90 max-w-3xl mx-auto">
            Understanding our cancellation terms and refund procedures for your peace of mind.
          </p>
          <div className="mt-6 text-sm opacity-75">
            Last updated: January 15, 2024
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Cancellation Image Banner */}
        <div className="relative rounded-lg overflow-hidden shadow-sm mb-12">
          <img
            src="https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
            alt="Calendar and cancellation policy"
            className="w-full h-48 object-cover"
          />
          <div className="absolute inset-0 bg-primary-900/70 flex items-center justify-center">
            <div className="text-center text-white px-4">
              <h2 className="text-2xl font-bold mb-2">Flexible Cancellation Policy</h2>
              <p className="text-lg">Transparent refund terms for your peace of mind</p>
            </div>
          </div>
        </div>

        {/* Cancellation Timeline */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-primary-900 mb-8 text-center">Cancellation Timeline</h2>
          <div className="space-y-6">
            {cancellationPeriods.map((period, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm border border-neutral-200">
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-12 h-12 ${period.bgColor} rounded-full flex items-center justify-center mr-4`}>
                        <period.icon className={`h-6 w-6 ${period.color}`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-primary-900">{period.period}</h3>
                        <p className="text-neutral-600">{period.refund}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-neutral-500">Processing Time</div>
                      <div className="font-medium text-primary-900">{period.processing}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Special Circumstances */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-primary-900 mb-8 text-center">Special Circumstances</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {specialCircumstances.map((circumstance, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm p-6 border border-neutral-200">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                  <ExclamationTriangleIcon className="h-6 w-6 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-primary-900 mb-3">{circumstance.title}</h3>
                <p className="text-neutral-600 mb-4">{circumstance.description}</p>
                <div>
                  <h4 className="font-semibold text-primary-900 mb-2">Requirements:</h4>
                  <ul className="space-y-1">
                    {circumstance.requirements.map((req, reqIndex) => (
                      <li key={reqIndex} className="flex items-start text-sm text-neutral-700">
                        <div className="w-2 h-2 bg-primary-600 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Refund Process */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-primary-900 mb-8 text-center">Refund Process</h2>
          <div className="space-y-8">
            {refundProcess.map((step, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm border border-neutral-200">
                <div className="p-6">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mr-6 flex-shrink-0">
                      <span className="text-xl font-bold text-primary-600">{step.step}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-primary-900 mb-2">{step.title}</h3>
                      <p className="text-neutral-600 mb-4">{step.description}</p>
                      <ul className="space-y-2">
                        {step.details.map((detail, detailIndex) => (
                          <li key={detailIndex} className="flex items-start text-neutral-700">
                            <div className="w-2 h-2 bg-primary-600 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Important Notes */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 mb-8">
          <div className="flex items-start">
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
              <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-yellow-900 mb-2">Important Notes</h3>
              <ul className="space-y-2 text-yellow-800">
                <li>• All cancellation requests must be submitted in writing via email</li>
                <li>• Processing fees may apply to all refunds (typically 3-5% of total amount)</li>
                <li>• Refunds are processed to the original payment method</li>
                <li>• No refunds for unused services or early departures</li>
                <li>• Group bookings may have different cancellation terms</li>
                <li>• Travel insurance is recommended to cover cancellation costs</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-primary-50 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-primary-900 mb-4">Need to Cancel Your Booking?</h2>
          <p className="text-neutral-600 mb-6">
            Contact us immediately to initiate the cancellation process:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-primary-900 mb-2">Cancellation Requests</h3>
              <p className="text-neutral-700">sighttrailsafaris@gmail.com</p>
              <p className="text-neutral-700">+254 708 560 665</p>
            </div>
            <div>
              <h3 className="font-semibold text-primary-900 mb-2">Emergency Contact</h3>
              <p className="text-neutral-700">24/7 Emergency Line</p>
              <p className="text-neutral-700">+254 700 000 001</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cancellation;
